import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useLaptopSplitActive } from '@/platform/laptopSplitState.ts';
import { ScreenplayElementView } from './ScreenplayElements.tsx';
import { AnnotationCanvas, AnnotationOverlay } from './AnnotationCanvas.tsx';
import type { StrokePath } from './AnnotationCanvas.tsx';
import { getOrderedElements, screenplayToPlainText } from '@/previs/screenplayHelpers.ts';
import { paginateScreenplay, CANONICAL_WIDTH, PAGE_HEIGHT_PX } from '@/previs/screenplayPaginator.ts';
import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';
import { getReviewerColor } from '@/utils/reviewerColor.ts';
import styles from './ScriptPanel.module.css';

/** Format an ISO 8601 date string into "Mar 3, 2026" style */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ScriptPanel() {
  const { t } = useTranslation('script');
  usePlaybackStore(s => s.currentTime); // subscribe for reactivity (re-render on time change)
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const getScriptProgress = useProjectStore(s => s.getScriptProgress);
  const customScriptTexts = useProjectStore(s => s.customScriptTexts);
  const setCustomScriptText = useProjectStore(s => s.setCustomScriptText);
  const editedScreenplaySceneIds = useProjectStore(s => s.editedScreenplaySceneIds);
  const readOnly = useProjectStore(s => s.readOnly);
  const toggleScriptPanel = useUiStore(s => s.toggleScriptPanel);
  const scriptAnnotationMode = useUiStore(s => s.scriptAnnotationMode);
  const comments = useCommentStore(s => s.comments);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyWrapRef = useRef<HTMLDivElement>(null);
  const pendingPathsRef = useRef<StrokePath[]>([]);
  const [bodyRect, setBodyRect] = useState({ width: 0, height: 0 });
  const [activeTool, setActiveTool] = useState<'pen' | 'highlighter' | 'eraser'>('pen');
  const [penColor, setPenColor] = useState<string | null>(null); // null = use reviewerColor
  const [highlighterColor, setHighlighterColor] = useState('#f5a623');
  const [annotationColorOpen, setAnnotationColorOpen] = useState(false);
  const [aPassMode, setAPassMode] = useState(false);
  const [aPassText, setAPassText] = useState<string | null>(null);
  const [aPassOriginal, setAPassOriginal] = useState<string | null>(null);

  const [initialPenPoint, setInitialPenPoint] = useState<{ clientX: number; clientY: number } | null>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);

  const project = useProjectStore(s => s.project);
  const hasScreenplay = project?.hasScreenplay ?? false;
  const lastModified = project?.lastModified;
  const { scene, progress } = getScriptProgress();
  const text = scene?.screenplayText;
  const screenplay = scene?.screenplay;
  const hasStructuredContent = !!screenplay;
  const sceneId = scene?.id ?? null;
  const isEdited = sceneId !== null && editedScreenplaySceneIds.has(sceneId);
  const customText = sceneId !== null ? (customScriptTexts[sceneId] ?? text ?? '') : '';

  // Ordered elements for structured rendering
  const elements = useMemo(
    () => screenplay ? getOrderedElements(screenplay) : [],
    [screenplay],
  );

  // Paginate elements into fixed-height pages (same layout as ScriptLining)
  const pages = useMemo(
    () => sceneId != null ? paginateScreenplay(elements, sceneId) : [],
    [elements, sceneId],
  );

  // Seed customScriptTexts from saved screenplayText on first open
  useEffect(() => {
    if (sceneId !== null && text && !screenplay && !(sceneId in customScriptTexts)) {
      setCustomScriptText(sceneId, text);
    }
  }, [sceneId, text, screenplay, customScriptTexts, setCustomScriptText]);

  // Reset Punch Up when scene changes
  const prevAPassSceneRef = useRef<number | null>(null);
  useEffect(() => {
    if (sceneId !== prevAPassSceneRef.current) {
      prevAPassSceneRef.current = sceneId;
      setAPassText(null);
      setAPassOriginal(null);
      setAPassMode(false);
    }
  }, [sceneId]);

  // Auto-scroll during playback (structured mode, not edited)
  useEffect(() => {
    if (!scrollRef.current || !hasStructuredContent || isEdited) return;
    if (isPlaying) {
      // Playback scroll — proportional
      const el = scrollRef.current;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return;

      const targetTop = progress * maxScroll;
      el.scrollTo({
        top: targetTop,
        behavior: 'instant',
      });
    }
  }, [progress, scene?.id, isPlaying, hasStructuredContent, isEdited]);


  // Measure body wrapper width for scale calculation
  useEffect(() => {
    const el = bodyWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setBodyRect({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scale factor: fit CANONICAL_WIDTH into available bodyWrap width
  const scaleFactor = bodyRect.width > 0 ? Math.min(1, bodyRect.width / CANONICAL_WIDTH) : 1;
  const scaledPageHeight = PAGE_HEIGHT_PX * scaleFactor;

  // ─── Prevent Apple Pencil from scrolling (pen is for drawing) ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: TouchEvent) => {
      if (!(e.target instanceof Node)) return;
      // Allow stylus taps on the bottom bar buttons
      if (bottomBarRef.current && bottomBarRef.current.contains(e.target)) return;
      // Don't interfere with the active annotation canvas
      if ((e.target as Element).closest?.(`.${styles.annotationActive}`)) return;
      for (let i = 0; i < e.touches.length; i++) {
        if ((e.touches[i] as unknown as Record<string, unknown>).touchType === 'stylus') {
          e.preventDefault();
          return;
        }
      }
    };
    el.addEventListener('touchstart', handler, { passive: false });
    return () => el.removeEventListener('touchstart', handler);
  }, []);

  // Annotation handlers
  const canAnnotate = capabilities.comments && !readOnly && hasStructuredContent && !isEdited;
  const reviewerName = useCommentStore(s => s.reviewerName);
  const reviewerColor = useMemo(() => getReviewerColor(reviewerName), [reviewerName]);

  const ANNOTATION_COLORS = ['#c43c2d', '#2d8cf0', '#333333', '#4caf50', '#f5a623', '#9c27b0'];
  const effectivePenColor = penColor ?? reviewerColor;

  // Computed canvas props based on active tool
  const canvasTool: 'pen' | 'eraser' = activeTool === 'eraser' ? 'eraser' : 'pen';
  const canvasStrokeColor = activeTool === 'highlighter' ? highlighterColor : effectivePenColor;
  const canvasStrokeWidth = activeTool === 'highlighter' ? 20 : 3;
  const canvasStrokeOpacity = activeTool === 'highlighter' ? 0.35 : 1;

  const handleEnterAnnotationMode = useCallback(() => {
    const ui = useUiStore.getState();
    if (!ui.scriptAnnotationMode) {
      ui.setScriptAnnotationMode(true);
      ui.setScriptAnnotationPage(1);
    }
  }, []);

  // ─── Apple Pencil auto-annotate ──────────────────────────────────
  const handlePenDown = useCallback((e: React.PointerEvent, pageNumber: number) => {
    if (e.pointerType !== 'pen') return;
    if (scriptAnnotationMode) return;
    if (!capabilities.comments || readOnly) return;
    setInitialPenPoint({ clientX: e.clientX, clientY: e.clientY });
    const ui = useUiStore.getState();
    ui.setScriptAnnotationMode(true);
    ui.setScriptAnnotationPage(pageNumber);
  }, [scriptAnnotationMode, readOnly]);

  // Clear initial pen point when annotation mode ends
  useEffect(() => {
    if (!scriptAnnotationMode) setInitialPenPoint(null);
  }, [scriptAnnotationMode]);

  const handleAnnotationPathsChange = useCallback((paths: StrokePath[]) => {
    pendingPathsRef.current = paths;
  }, []);

  const handleAnnotationSave = useCallback(() => {
    const ui = useUiStore.getState();
    const paths = pendingPathsRef.current;
    const page = ui.scriptAnnotationPage ?? 1;
    if (paths.length > 0 && sceneId != null) {
      const data = { paths: [...paths], viewBox: '0 0 1 1' };
      useCommentStore.getState().addComment(
        null, null, t('screenplayAnnotationAdded'), undefined,
        JSON.stringify(data), page, sceneId,
      );
    }
    pendingPathsRef.current = [];
    ui.setScriptAnnotationMode(false);
    setActiveTool('pen');
  }, [sceneId, t]);

  const handleAnnotationDiscard = useCallback(() => {
    pendingPathsRef.current = [];
    useUiStore.getState().setScriptAnnotationMode(false);
    setActiveTool('pen');
  }, []);


  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (scriptAnnotationMode) {
          useUiStore.getState().setScriptAnnotationMode(false);
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [scriptAnnotationMode]);

  // Auto-save annotation on outside click
  useEffect(() => {
    if (!scriptAnnotationMode) return;
    const handler = (e: PointerEvent) => {
      const bodyEl = bodyWrapRef.current;
      if (!bodyEl) return;
      if (bodyEl.contains(e.target as Node)) return;
      if (bottomBarRef.current?.contains(e.target as Node)) return;
      handleAnnotationSave();
    };
    window.addEventListener('pointerdown', handler, true);
    return () => window.removeEventListener('pointerdown', handler, true);
  }, [scriptAnnotationMode, handleAnnotationSave]);

  // Clear annotations handler (for normal mode trash button)
  const sceneAnnotationComments = useMemo(() => {
    if (sceneId == null) return [];
    return comments.filter(c => c.sceneId === sceneId && c.screenplayPage != null && c.annotationSvg);
  }, [comments, sceneId]);

  const [clearAnnotationsConfirm, setClearAnnotationsConfirm] = useState(false);
  const [revertPunchUpConfirm, setRevertPunchUpConfirm] = useState(false);

  const handleRevertPunchUp = useCallback(() => {
    if (!screenplay) return;
    const plain = screenplayToPlainText(screenplay);
    setAPassText(plain);
    setAPassOriginal(plain);
    setRevertPunchUpConfirm(false);
  }, [screenplay]);

  const handleClearAllAnnotations = useCallback(() => {
    if (sceneId == null) return;
    const cs = useCommentStore.getState();
    for (const c of sceneAnnotationComments) {
      cs.deleteComment(c.id);
    }
    setClearAnnotationsConfirm(false);
  }, [sceneId, sceneAnnotationComments]);

  // Per-page annotation data (latest snapshot per page — same approach as ScriptLining)
  const scriptAnnotationPage = useUiStore(s => s.scriptAnnotationPage);

  const latestPageAnnotation = useMemo(() => {
    const map = new Map<number, string>();
    const best = new Map<number, number>(); // page → highest comment id
    for (const c of comments) {
      if (c.screenplayPage != null && c.sceneId === sceneId && c.annotationSvg) {
        const prev = best.get(c.screenplayPage) ?? -1;
        if (c.id > prev) {
          best.set(c.screenplayPage, c.id);
          map.set(c.screenplayPage, c.annotationSvg);
        }
      }
    }
    return map;
  }, [comments, sceneId]);

  // Pre-load latest annotation paths for the active annotation page
  const latestAnnotationPathsForPage = useMemo((): StrokePath[] => {
    if (scriptAnnotationPage == null) return [];
    const svg = latestPageAnnotation.get(scriptAnnotationPage);
    if (!svg) return [];
    try {
      const data = JSON.parse(svg) as { paths?: StrokePath[] };
      return data.paths ?? [];
    } catch { return []; }
  }, [latestPageAnnotation, scriptAnnotationPage]);

  const handleEnterAPass = useCallback(() => {
    if (!screenplay) return;
    if (aPassText === null) {
      const plain = screenplayToPlainText(screenplay);
      setAPassText(plain);
      setAPassOriginal(plain);
    }
    setAPassMode(true);
  }, [screenplay, aPassText]);

  const handleExitAPass = useCallback(() => {
    setAPassMode(false);
  }, []);

  const isMobile = useMobileLayout();
  // 20260929 - the laptop split's bottom region is too short for a side panel (owner: "make the
  // Script/Comments panels fullscreen"), so there the panel is the phone's fullscreen overlay too: the
  // stylesheet's `:global(.laptop-split)` selector does the layout, this does the portal.
  const laptopSplitActive = useLaptopSplitActive();
  const fullscreenOverlay = isMobile || laptopSplitActive;
  const panelCls = `${styles.scriptPanel} ${readOnly ? styles.panelReview : ''}`;

  // Portal helper — escape viewerWrapper stacking context when the panel is the fullscreen overlay
  const maybePortal = (node: React.ReactElement) =>
    fullscreenOverlay ? createPortal(node, document.body) : node;

  // Detect user edits in plain-text mode (customText differs from the scene's original text)
  const hasPlainTextEdits = !hasStructuredContent && sceneId !== null
    && (sceneId in customScriptTexts) && customText !== (text ?? '');

  // Info bar message
  const infoBarText = aPassMode
    ? t('infoBarRevisionsUnsaved')
    : (isEdited || hasPlainTextEdits)
      ? t('infoBarTempShotScript')
      : lastModified
        ? t('infoBarLastModified', { date: formatDate(lastModified) })
        : null;

  // Scene heading text. filter(Boolean)-joined so an empty location name (no location set)
  // doesn't leave a stray double space — mirrors composeSceneCaptionRest (sceneCaption.ts).
  const headingText = scene
    ? [
        [scene.locationType ? `${scene.locationType}.` : '', scene.name.toUpperCase()].filter(Boolean).join(' '),
        scene.timeOfDay,
      ].filter(Boolean).join(' — ')
    : t('scriptHeadingDefault');

  // Show text mode? (plain-text-only scenes OR edited-from-structured)
  const showTextMode = hasScreenplay && (!hasStructuredContent || isEdited);

  // ── No screenplay imported — empty state ──────────────────────────
  if (!hasScreenplay) {
    return maybePortal(
      <div className={panelCls}>
        <div className={styles.headingRow}>
          <div className={styles.sceneHeading} style={{ flex: 1 }}>{t('scriptHeadingDefault')}</div>
          <button className={styles.closeBtn} onClick={() => capabilities.bridge ? enqueueSendToNative('openExternalUrl', { url: 'https://www.previspro.com/learn/final-draft' }) : window.open('https://www.previspro.com/learn/final-draft', '_blank')} title={t('helpButtonTitle')}>?</button>
          <button className={styles.closeBtn} onClick={toggleScriptPanel} title={t('closeScriptPanelTitle')}>Ⓧ</button>
        </div>
        <div className={styles.emptyState}>{t('noScriptImported')}</div>
      </div>
    );
  }

  // ── Text mode (plain text OR edited-from-structured) ──────────────
  if (showTextMode) {
    return maybePortal(
      <div className={panelCls}>
        {infoBarText && (
          <div className={styles.infoBar}>
            <span className={styles.lastModified}>{infoBarText}</span>
            <button className={styles.closeBtn} onClick={() => capabilities.bridge ? enqueueSendToNative('openExternalUrl', { url: 'https://www.previspro.com/learn/final-draft' }) : window.open('https://www.previspro.com/learn/final-draft', '_blank')} title={t('helpButtonTitle')}>?</button>
          <button className={styles.closeBtn} onClick={toggleScriptPanel} title={t('closeScriptPanelTitle')}>Ⓧ</button>
          </div>
        )}
        <div className={styles.headingRow}>
          <div className={styles.sceneHeading} style={{ flex: 1 }}>{headingText}</div>
          {!infoBarText && (<>
            <button className={styles.closeBtn} onClick={() => capabilities.bridge ? enqueueSendToNative('openExternalUrl', { url: 'https://www.previspro.com/learn/final-draft' }) : window.open('https://www.previspro.com/learn/final-draft', '_blank')} title={t('helpButtonTitle')}>?</button>
            <button className={styles.closeBtn} onClick={toggleScriptPanel} title={t('closeScriptPanelTitle')}>Ⓧ</button>
          </>)}
        </div>
        <div className={`${styles.scriptBody} ${readOnly ? styles.readOnly : ''}`}>
          <textarea
            className={styles.editableArea}
            placeholder={t('pasteScriptPlaceholder')}
            value={customText}
            readOnly={readOnly}
            onChange={e => sceneId !== null && setCustomScriptText(sceneId, e.target.value)}
          />
        </div>
      </div>
    );
  }

  // ── Structured mode ─────────────────────────────────────────────
  return maybePortal(
    <div className={panelCls}>
      {infoBarText && (
        <div className={`${styles.infoBar} ${aPassMode ? styles.aPassInfoBar : ''}`}>
          <span className={styles.lastModified}>{infoBarText}</span>
          {aPassMode && (() => {
            const dirty = aPassText !== aPassOriginal;
            return (
              <button
                className={`${styles.revertBtn} ${!dirty ? styles.revertBtnDisabled : ''}`}
                onClick={dirty ? () => setRevertPunchUpConfirm(true) : undefined}
                title={t('revertToOriginalScriptTitle')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
            );
          })()}
          <button className={styles.closeBtn} onClick={() => capabilities.bridge ? enqueueSendToNative('openExternalUrl', { url: 'https://www.previspro.com/learn/final-draft' }) : window.open('https://www.previspro.com/learn/final-draft', '_blank')} title={t('helpButtonTitle')}>?</button>
          <button className={styles.closeBtn} onClick={toggleScriptPanel} title={t('closeScriptPanelTitle')}>Ⓧ</button>
        </div>
      )}
      <div className={`${styles.headingRow} ${aPassMode ? styles.aPassHeading : ''}`}>
        <div className={styles.sceneHeading} style={{ flex: 1 }}>{headingText}</div>
        {!infoBarText && (<>
          <button className={styles.closeBtn} onClick={() => capabilities.bridge ? enqueueSendToNative('openExternalUrl', { url: 'https://www.previspro.com/learn/final-draft' }) : window.open('https://www.previspro.com/learn/final-draft', '_blank')} title={t('helpButtonTitle')}>?</button>
          <button className={styles.closeBtn} onClick={toggleScriptPanel} title={t('closeScriptPanelTitle')}>Ⓧ</button>
        </>)}
      </div>

      {/* Punch Up mode — editable copy */}
      {aPassMode ? (
        <div className={`${styles.scriptBody} ${styles.aPassBody}`}>
          <textarea
            className={`${styles.editableArea} ${styles.punchUpText}`}
            value={aPassText ?? ''}
            onChange={e => setAPassText(e.target.value)}
            placeholder={t('editRevisionsPlaceholder')}
          />
        </div>
      ) : (
      <div ref={bodyWrapRef} className={styles.bodyWrap}>
        <div ref={scrollRef} className={`${styles.scriptBody} ${readOnly ? styles.readOnly : ''}`}>
          {pages.map((page) => {
            const isActivePage = scriptAnnotationMode && scriptAnnotationPage === page.pageNumber;
            const svgForPage = latestPageAnnotation.get(page.pageNumber);

            return (
              <div
                key={page.pageNumber}
                className={styles.pageWrapper}
                style={{ height: scaledPageHeight }}
                onPointerDown={e => handlePenDown(e, page.pageNumber)}
              >
                <div
                  className={styles.scriptText}
                  style={{
                    width: CANONICAL_WIDTH,
                    height: PAGE_HEIGHT_PX,
                    transform: `scale(${scaleFactor})`,
                    transformOrigin: 'top left',
                  }}
                >
                  {page.elements.map((el, elIdxInPage) => {
                    const globalIdx = page.startElementIndex + elIdxInPage;
                    return (
                      <div
                        key={globalIdx}
                        className={styles.elementRow}
                      >
                        <ScreenplayElementView element={el} />
                      </div>
                    );
                  })}
                </div>

                {/* Read-only annotation overlay — latest snapshot for this page */}
                {svgForPage && !isActivePage && (
                  <div className={styles.annotationOverlay}>
                    <AnnotationOverlay
                      svgJson={svgForPage}
                      contentRect={{ x: 0, y: 0, width: CANONICAL_WIDTH * scaleFactor, height: scaledPageHeight }}
                    />
                  </div>
                )}

                {/* Active annotation canvas — pre-loaded with latest snapshot */}
                {isActivePage && (
                  <div className={`${styles.annotationOverlay} ${styles.annotationActive}`}>
                    <AnnotationCanvas
                      contentRect={{ x: 0, y: 0, width: CANONICAL_WIDTH * scaleFactor, height: scaledPageHeight }}
                      onPathsChange={handleAnnotationPathsChange}
                      strokeColor={canvasStrokeColor}
                      onFinish={handleAnnotationSave}
                      initialPoint={initialPenPoint}
                      initialPaths={latestAnnotationPathsForPage}
                      tool={canvasTool}
                      strokeWidth={canvasStrokeWidth}
                      strokeOpacity={canvasStrokeOpacity}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Annotation toolbar — shown above footer when in annotation mode */}
      {scriptAnnotationMode && !aPassMode && (
        <div ref={bottomBarRef} className={styles.bottomBar}>
          <div className={`${styles.colorPickerWrap} ${activeTool === 'eraser' ? styles.colorDotDimmed : ''}`}>
            {annotationColorOpen ? (
              <div className={styles.colorExpandedRow}>
                {ANNOTATION_COLORS.map(c => (
                  <button
                    key={c}
                    className={`${styles.colorDot} ${(activeTool === 'pen' ? effectivePenColor : highlighterColor) === c ? styles.colorDotActive : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => {
                      if (activeTool === 'pen') setPenColor(c);
                      else if (activeTool === 'highlighter') setHighlighterColor(c);
                      setAnnotationColorOpen(false);
                    }}
                  />
                ))}
              </div>
            ) : (
              <button
                className={styles.colorSwatchTrigger}
                style={{ backgroundColor: activeTool === 'highlighter' ? highlighterColor : effectivePenColor }}
                onClick={() => { if (activeTool !== 'eraser') setAnnotationColorOpen(true); }}
                title={t('changeColorTitle')}
              />
            )}
          </div>
          <div className={styles.toolBtnGroup}>
            <button
              className={`${styles.toolBtn} ${activeTool === 'pen' ? styles.toolBtnActive : ''}`}
              onClick={() => { setActiveTool('pen'); setAnnotationColorOpen(false); }}
              title={t('penTitle')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </button>
            <button
              className={`${styles.toolBtn} ${activeTool === 'highlighter' ? styles.toolBtnActive : ''}`}
              onClick={() => { setActiveTool('highlighter'); setAnnotationColorOpen(false); }}
              title={t('highlighterTitle')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l-6 6v3h9l3-3" /><path d="M22 12l-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" />
              </svg>
            </button>
            <button
              className={`${styles.toolBtn} ${activeTool === 'eraser' ? styles.toolBtnActive : ''}`}
              onClick={() => { setActiveTool('eraser'); setAnnotationColorOpen(false); }}
              title={t('eraserTitle')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 20H7L3 16c-.8-.8-.8-2 0-2.8L14.8 1.4c.8-.8 2-.8 2.8 0L21 4.8c.8.8.8 2 0 2.8L10 18" />
              </svg>
            </button>
          </div>
          <div className={styles.annotationActions}>
            <button className={styles.discardBtn} onClick={handleAnnotationDiscard} title={t('discardAnnotationTitle')}>
              {'\u2715'}
            </button>
            <button className={styles.saveBtn} onClick={handleAnnotationSave} title={t('saveAnnotationTitle')}>
              {'\u2713'}
            </button>
          </div>
        </div>
      )}

      {/* Stable footer — mode toggle + pencil/trash */}
      {!readOnly && hasStructuredContent && (
        <div className={`${styles.modeToggle} ${aPassMode ? styles.modeToggleAPass : ''}`}>
          {sceneAnnotationComments.length > 0 && !aPassMode && (
            <button className={`${styles.statusBtn} ${styles.footerIconBtn}`} onClick={() => setClearAnnotationsConfirm(true)} title={t('clearAnnotationsTitle')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          )}
          <button
            className={`${styles.modeBtn} ${!aPassMode ? styles.modeBtnActive : ''}`}
            onClick={handleExitAPass}
          >
            {t('modeBtnScript')}
          </button>
          <button
            className={`${styles.modeBtn} ${aPassMode ? styles.modeBtnActive : ''}`}
            onClick={handleEnterAPass}
          >
            {t('modeBtnPunchUp')}
          </button>
          {canAnnotate && (
            <button
              className={`${styles.pencilBtn} ${styles.footerPencil} ${aPassMode || isPlaying ? styles.pencilDisabled : ''}`}
              onClick={aPassMode || isPlaying ? undefined : handleEnterAnnotationMode}
              disabled={isPlaying}
              style={isPlaying ? { opacity: 0.35 } : undefined}
              title={t('drawOnScriptTitle')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Clear annotations confirmation dialog */}
      {clearAnnotationsConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setClearAnnotationsConfirm(false)}>
          <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <div className={styles.confirmTitle}>{t('clearAnnotationsDialogTitle')}</div>
            <div className={styles.confirmBody}>
              {t('clearAnnotationsDialogBody')}
            </div>
            <div className={styles.confirmButtons}>
              <button className={styles.confirmBtnCancel} onClick={() => setClearAnnotationsConfirm(false)}>{t('cancelButton')}</button>
              <button className={styles.confirmBtnDanger} onClick={handleClearAllAnnotations}>{t('clearAllButton')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Revert punch up confirmation dialog */}
      {revertPunchUpConfirm && (
        <div className={styles.confirmOverlay} onClick={() => setRevertPunchUpConfirm(false)}>
          <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <div className={styles.confirmTitle}>{t('revertPunchUpDialogTitle')}</div>
            <div className={styles.confirmBody}>
              {t('revertPunchUpDialogBody')}
            </div>
            <div className={styles.confirmButtons}>
              <button className={styles.confirmBtnCancel} onClick={() => setRevertPunchUpConfirm(false)}>{t('cancelButton')}</button>
              <button className={styles.confirmBtnDanger} onClick={handleRevertPunchUp}>{t('revertButton')}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
